import { useDebounceFn, useMemoizedFn, useMount } from 'ahooks';
import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxNoProofTip } from 'components/RevxNoProofTip';
import { RevxSpin } from 'components/RevxSpin';
import { CartLineItem } from 'domains/cartDomain';
import {
  AdjustMarkupPayload,
  JobConfigViewer,
  JobEstimator,
  JobEstimatorComparatorItem,
  PopoverType,
} from 'domains/jobconfigDomain';
import { useJobRevision } from 'hooks/useJobRevision';
import { cloneDeep, isBoolean, isNumber, isUndefined } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { QUANTITY_DEBOUNCE_MILLISECOND } from 'utils/constants';
import { StepButtons } from '../ConfigStep/ActionButtons';
import './SupplierComparison.less';
import './ViewQuote.less';
import { NoOtherSupplier } from './comparison/NoOtherSupplier';
import { SupplierComparisonItem } from './comparison/SupplierComparisonItem';
import { SupplierComparisonLabel } from './comparison/SupplierComparisonLabel';

interface Props {
  isExpert?: boolean;
  jobConfigViewer?: JobConfigViewer;
  addToCArtHandler?: (e: CartLineItem) => void;
  isProject?: boolean;
}

export const SupplierComparison = (props: Props) => {
  const configItem = useAppSelector((state) => state.jobConfig.configItem);
  const [popoverVisible, setPopoverVisible] = useState<number | false>(false);
  const partSpec = configItem?.specItems.find((i) => i.selected)?.spec;
  const [popoverHeight, setPopoverHeight] = useState<number>();
  const [popoverType, setpopoverType] = useState<PopoverType>();
  const [jobEstimator, setJobEstimator] = useState<JobEstimator>();
  const [choiceSerialNumber, setChoiceSerialNumber] = useState<number>(
    configItem?.specItems[0].serialNumber || 0
  );
  const [jobEstimatorLoading, setJobEstimatorLoading] = useState<boolean>(true);
  const [showList, setShowList] = useState<string[]>([
    'Services',
    'Shipping Total',
  ]);
  const [boxHeight, setBoxHeight] = useState<number>(40);
  const [labelMaxWidth, setLabelMaxWidth] = useState<number>(0);

  const { isExpert, jobConfigViewer, addToCArtHandler } = props;

  const dispatch = useAppDispatch();
  const uiStatusFilter = useAppSelector(
    (state) => state.workspace.uiStatusFilter
  );
  const cacheChoiceSerialNumber = useAppSelector(
    (state) => state.jobEstimator.choiceSerialNumber
  );
  const [form] = useForm();
  const isJobRevision = useJobRevision(configItem);
  const history = useHistory();

  useMount(async () => {
    if (!jobConfigViewer) {
      defaultComparison();
    }
  });

  const maxLabel = useMemo(() => {
    return 240;
  }, []);

  const defaultComparison = useMemoizedFn(
    async (SerialNumber: number = choiceSerialNumber) => {
      setJobEstimatorLoading(true);
      try {
        let res = jobEstimator;
        if (
          !jobEstimator?.quantityItems.find(
            (item) => item.serialNumber === SerialNumber
          )
        ) {
          res = await dispatch.jobEstimator.generate({
            jobConfigId: configItem?.id || jobConfigViewer?.id || '',
            quantityItemSerialNumber: SerialNumber,
          });
          res?.quantityItems.map((item) => {
            const comparatorItem = [
              ...item.comparatorItems.map((item) => {
                item.averageEligible = true;
                item.revshopEligible = true;
                return item;
              }),
            ];
            const isAverageNoEligible =
              !item.averageEligible &&
              !item.comparatorItems.find((item) => item.serialNumber === -1) &&
              Boolean(item.comparatorItems.length);
            const isRevshopNoEligible =
              (!item.revshopEligible &&
                !item.comparatorItems.find(
                  (item) => item.serialNumber === -2
                ) &&
                Boolean(item.comparatorItems.length)) ||
              isJobRevision;
            if (isAverageNoEligible) {
              comparatorItem.unshift({
                ...item.comparatorItems[0],
                averageEligible: false,
                revshopEligible: true,
                serialNumber: -1,
              } as JobEstimatorComparatorItem);
            }
            if (isRevshopNoEligible) {
              comparatorItem.splice(1, 0, {
                ...item.comparatorItems[0],
                averageEligible: true,
                revshopEligible: false,
                serialNumber: -2,
              } as JobEstimatorComparatorItem);
            }
            if (isAverageNoEligible || isRevshopNoEligible) {
              item.comparatorItems = [...comparatorItem];
            } else {
              item.comparatorItems = item.comparatorItems.map((item) => {
                item.averageEligible = true;
                item.revshopEligible = true;
                return item;
              });
            }
            return item;
          });
          // res = await dispatch.jobEstimator.getJobEstimator({
          //   jobConfigId: configItem.id,
          // });
          setJobEstimator(res);
        }
        if (isUndefined(choiceSerialNumber)) {
          setChoiceSerialNumber(configItem?.specItems[0].serialNumber);
        }

        setTimeout(() => {
          let maxHeight = 40;
          for (let i in document.getElementsByClassName('jobSvcRef')) {
            const item = document.getElementsByClassName('jobSvcRef')[i];
            if (item?.scrollHeight > maxHeight) {
              maxHeight = item?.scrollHeight;
            }
          }
          setBoxHeight(maxHeight);
          setLabelMaxWidth(
            (
              document.getElementsByClassName(
                'comparison-drawer-label'
              )?.[1] as HTMLElement
            )?.offsetWidth
          );
        });
      } catch {
      } finally {
        setJobEstimatorLoading(false);
      }
    }
  );

  useEffect(() => {
    setChoiceSerialNumber(cacheChoiceSerialNumber);
  }, [cacheChoiceSerialNumber]);

  useEffect(() => {
    if (jobConfigViewer) {
      defaultComparison();
    }
  }, [jobConfigViewer, defaultComparison]);

  const updateComparison = async (
    jobEstimatorComparatorItem: JobEstimatorComparatorItem
  ) => {
    setJobEstimatorLoading(true);
    try {
      const jobEstimatorClone = cloneDeep(jobEstimator);
      let serialNumberIdx = 0;
      const idx = jobEstimatorClone?.quantityItems
        .find((item, idx) => {
          if (item.serialNumber === choiceSerialNumber) {
            serialNumberIdx = idx;
          }
          return item.serialNumber === choiceSerialNumber;
        })
        ?.comparatorItems.map((item) => item.serialNumber)
        .indexOf(jobEstimatorComparatorItem.serialNumber);
      if (isNumber(idx)) {
        jobEstimatorClone?.quantityItems[
          serialNumberIdx
        ].comparatorItems.splice(idx, 1, {
          ...jobEstimatorComparatorItem,
          averageEligible: true,
          revshopEligible: true,
        });
        setJobEstimator(jobEstimatorClone);
      }
    } catch {
    } finally {
      setJobEstimatorLoading(false);
    }
  };

  const defaultForm = useMemoizedFn((list: JobEstimatorComparatorItem[]) => {
    let defaultFormData: { [x: string]: any } = {};
    list.forEach((item) => {
      if (!defaultFormData[item.serialNumber + '']) {
        defaultFormData[item.serialNumber + ''] = {};
      }
      item?.comparator?.parts?.forEach((part) => {
        if (!defaultFormData[item.serialNumber + ''][part.skuId + '']) {
          defaultFormData[item.serialNumber + ''][part.skuId + ''] = {};
        }
        defaultFormData[item.serialNumber + ''][part.skuId + '']['curSelect'] =
          part.revxPressId;
      });
    });
    form.setFieldsValue(defaultFormData);
  });

  const revxMarkupPrice = useMemoizedFn(
    (price: number, revxMarkupPct?: number, globalMarkupPct?: number) => {
      return +(
        price *
        (1 + Number(revxMarkupPct) / (100 - Number(revxMarkupPct))) *
        (1 + +(globalMarkupPct || 0) / 100)
      ).toFixed(2);
    }
  );

  // const globalMarkupPrice = useMemoizedFn(
  //   (price: number, revxMarkupPct?: number, globalMarkupPct?: number) => {
  //     return +(
  //       price *
  //       (1 +
  //         +(revxMarkupPct || 0) / 100 +
  //         ((+(revxMarkupPct || 0) / 100) * +(globalMarkupPct || 0)) / 100)
  //     ).toFixed(2);
  //   }
  // );

  const showJobEstimator = useMemo(() => {
    const list = cloneDeep(
      jobEstimator?.quantityItems.find(
        (item) => item.serialNumber === choiceSerialNumber
      )?.comparatorItems || []
    );
    setShowList((s) => [
      ...s,
      ...showJobEstimator.partLabel?.map((part) => part.skuName),
    ]);
    const partLabelSvc: any[] = [];
    list.forEach((item) => {
      for (let i = 0; i < item.comparator?.parts?.length; i++) {
        const curLabelSvc = partLabelSvc.find(
          (partSvcItem) => partSvcItem.skuId === item.comparator?.parts[i].skuId
        );
        if (!curLabelSvc) {
          partLabelSvc.push({
            skuId: item.comparator?.parts[i].skuId,
            svcs: item.comparator?.parts[i].svcs
              .filter((sItem) => sItem.charge)
              .map((sItem) => sItem.name),
          });
        } else {
          curLabelSvc.svcs = Array.from(
            new Set([
              ...curLabelSvc.svcs,
              ...item.comparator?.parts[i].svcs
                .filter((sItem) => sItem.charge)
                .map((sItem) => sItem.name),
            ])
          );
        }
      }
    });

    list
      // .filter((item) => item.serialNumber > -1)
      .forEach((item, idx) => {
        let priceTotal = 0;
        if (item && item.comparator) {
          for (let i = 0; i < item.comparator?.parts?.length; i++) {
            const curPartSvc = item.comparator?.parts[i].svcs;
            const curLabelSvc = partLabelSvc.find(
              (partSvcItem) =>
                partSvcItem.skuId === item.comparator?.parts[i].skuId
            );
            curLabelSvc.svcs.forEach((defaultItem: string) => {
              if (!curPartSvc.find((partSvc) => partSvc.name === defaultItem)) {
                curPartSvc.push({
                  name: defaultItem,
                  charge: 0,
                });
              }
            });
            for (let i = 0; i < curPartSvc.length; i++) {
              if (
                !curLabelSvc.svcs.find(
                  (defaultItem: string) => defaultItem === curPartSvc[i].name
                )
              ) {
                curPartSvc.splice(i, 1);
              }
            }

            if (
              (idx === 0 && item.serialNumber > -1) ||
              (idx === 1 && item.serialNumber > -1)
            ) {
              item.comparator.parts[i].paperCharge = revxMarkupPrice(
                item.comparator.parts[i].paperCharge,
                item.comparator.revxMarkupPct,
                item.comparator.supplierMarkupPct
              );
              item.comparator.parts[i].pressCharge = revxMarkupPrice(
                item.comparator.parts[i].pressCharge,
                item.comparator.revxMarkupPct,
                item.comparator.supplierMarkupPct
              );
              priceTotal += item.comparator.parts[i].paperCharge;
              priceTotal += item.comparator.parts[i].pressCharge;
              for (let j = 0; j < item.comparator.parts[i].svcs.length; j++) {
                item.comparator.parts[i].svcs[j].charge = revxMarkupPrice(
                  item.comparator.parts[i].svcs[j].charge,
                  item.comparator.revxMarkupPct,
                  item.comparator.supplierMarkupPct
                );
                priceTotal += item.comparator.parts[i].svcs[j].charge;
              }
            }
          }
          if (
            (idx === 0 && item.serialNumber > -1) ||
            (idx === 1 && item.serialNumber > -1)
          ) {
            for (let j = 0; j < item?.comparator?.svcs.length; j++) {
              item.comparator.svcs[j].charge = revxMarkupPrice(
                item.comparator.svcs[j].charge,
                item.comparator.revxMarkupPct,
                item.comparator.supplierMarkupPct
              );
              priceTotal += item.comparator.svcs[j].charge;
            }
            if (item?.comparator?.parcel) {
              if (!item?.boxChargeEditable) {
                item.comparator.parcel.charge = revxMarkupPrice(
                  item.comparator?.parcel.charge,
                  item.comparator.revxMarkupPct,
                  item.comparator.supplierMarkupPct
                );
              }
              priceTotal += item.comparator?.parcel.charge;
            }
            if (item.comparator.parcel.oldPaperCharge) {
              item.comparator.parts[0].paperCharge =
                item.comparator.parcel.oldPaperCharge;
              return;
            }
            if (priceTotal !== item?.comparator?.revxPrintingCharge) {
              item.comparator.parts[0].paperCharge =
                item.comparator.parts[0].paperCharge +
                (item.comparator.revxPrintingCharge - priceTotal);
            }
          }
        }
      });

    defaultForm(list);
    return {
      list,
      partLabel: list?.[0]?.comparator?.parts || [],
      svcLabel: list?.[0]?.comparator?.svcs || [],
      rush: cloneDeep(
        jobEstimator?.quantityItems.find(
          (item) => item.serialNumber === choiceSerialNumber
        )?.rush
      ),
    };
  }, [choiceSerialNumber, jobEstimator, defaultForm, revxMarkupPrice]);

  const clickToggle = useMemoizedFn((x: string) => {
    if (showList.find((item) => item === x)) {
      setShowList(showList.filter((item) => item !== x));
    } else {
      setShowList([...showList, x]);
    }
  });

  const { run: updateMarkup, cancel: cancelUpdateMarkup } = useDebounceFn(
    async (payload: AdjustMarkupPayload) => {
      setJobEstimatorLoading(true);
      try {
        if (Object.keys(payload).includes('parcelCost')) {
          const res = await dispatch.jobEstimator.adjustParcel(payload);
          updateComparison({
            ...res,
            comparator: {
              ...res.comparator,
              parcel: {
                ...res.comparator.parcel,
                oldPaperCharge: payload.paperCharge,
              },
            },
          });
        } else {
          const res = await dispatch.jobEstimator.adjustMarkup(payload);
          updateComparison(res);
        }
      } catch {
      } finally {
        setJobEstimatorLoading(false);
      }
    },
    { wait: QUANTITY_DEBOUNCE_MILLISECOND }
  );

  const isClickToggle = useMemoizedFn((x: string) => {
    return Boolean(showList.find((item) => item === x));
  });

  const containerMaxWidth = useMemo(() => {
    return {
      maxWidth: `${6 * maxLabel + 4}px`,
    };
  }, [maxLabel]);

  const popoverStyle = useMemo(() => {
    if (!popoverHeight) {
      return {};
    }
    if (popoverType === PopoverType.shipping) {
      return {
        bottom: '350px',
        minHeight: popoverHeight! + 20 || 0,
      };
    } else if (popoverType === PopoverType.shipDate) {
      return {
        top: '110px',
        minHeight: popoverHeight! + 20 || 0,
      };
    } else if (popoverType === PopoverType.arrivalDate) {
      return {
        top: '150px',
        minHeight: popoverHeight! + 20 || 0,
      };
    } else {
      return {};
    }
  }, [popoverHeight, popoverType]);

  const cancelJobHandler = async () => {
    try {
      await dispatch.jobConfig.jobRevisionCancel(configItem?.revisionId);
    } catch {}
    history.push(`/workspace/${uiStatusFilter}`);
  };

  return (
    <>
      <RevxSpin spinning={jobEstimatorLoading} className="comparison">
        {Boolean(showJobEstimator.list.length) && (
          <>
            {!Boolean(jobConfigViewer) && (
              <div className="step-heading">cost analysis</div>
            )}
            <RevxFlexLayout
              justify="space-between"
              align="bottom"
              className="view-quote-drawer comparison-drawer"
              style={containerMaxWidth}
            >
              <RevxFlexLayout
                className="comparison-drawer-header-title"
                align="center"
                style={{
                  position: 'relative',
                  top: '0px',
                }}
              >
                <h5
                  style={{
                    marginRight: '20px',
                  }}
                >
                  Job Estimator
                </h5>

                <RevxButton
                  hidden={!isJobRevision}
                  brandGreen
                  style={{
                    marginRight: '20px',
                    width: 120,
                  }}
                  // className="btn-supplier-comparison"
                  onClick={() => cancelJobHandler()}
                >
                  <Trans i18nKey="common.cancel"></Trans>
                </RevxButton>

                <RevxNoProofTip
                  show={
                    !jobEstimator?.digitalProofRequired && !jobEstimatorLoading
                  }
                />
              </RevxFlexLayout>
              <RevxFlexLayout
                className="comparison-drawer-header"
                hidden={!jobEstimator}
              >
                {Boolean(jobConfigViewer) && (
                  <label className="comparison-drawer-header-active">
                    <span>{jobConfigViewer?.quantity}</span>
                  </label>
                )}
                {configItem?.specItems.map((item) => (
                  <label
                    key={item.serialNumber}
                    className={
                      choiceSerialNumber === item.serialNumber
                        ? `comparison-drawer-header-active`
                        : ''
                    }
                    onClick={() => {
                      setChoiceSerialNumber(item.serialNumber);
                      dispatch.jobEstimator.setState({
                        choiceSerialNumber: item.serialNumber,
                      });
                      defaultComparison(item.serialNumber);
                    }}
                  >
                    <span>{item.quantity}</span>
                  </label>
                ))}
              </RevxFlexLayout>
            </RevxFlexLayout>
            <Form
              form={form}
              hidden={!jobEstimator}
              style={{ ...containerMaxWidth, position: 'relative' }}
            >
              <RevxFlexLayout className="comparison-drawer-body" align="top">
                <SupplierComparisonLabel
                  maxLabel={maxLabel}
                  showJobEstimator={showJobEstimator}
                  clickToggle={clickToggle}
                  isClickToggle={isClickToggle}
                  boxHeight={boxHeight}
                  canAddToCart={
                    configItem?.canAddToCart || Boolean(jobConfigViewer)
                  }
                  jobEstimator={jobEstimator}
                  popoverHeight={popoverHeight}
                  popoverVisible={popoverVisible}
                  popoverType={popoverType}
                  revisionId={configItem?.revisionId}
                  spec={partSpec}
                />

                <SupplierComparisonItem
                  showJobEstimator={showJobEstimator}
                  maxLabel={maxLabel}
                  clickToggle={clickToggle}
                  isClickToggle={isClickToggle}
                  labelMaxWidth={labelMaxWidth}
                  boxHeight={boxHeight}
                  jobEstimator={jobEstimator}
                  choiceSerialNumber={choiceSerialNumber}
                  updateComparison={updateComparison}
                  setJobEstimatorLoading={setJobEstimatorLoading}
                  canAddToCart={
                    configItem?.canAddToCart || Boolean(jobConfigViewer)
                  }
                  revisionId={configItem?.revisionId}
                  cancelUpdateMarkup={cancelUpdateMarkup}
                  updateMarkup={updateMarkup}
                  setPopoverVisible={setPopoverVisible}
                  popoverVisible={popoverVisible}
                  setpopoverType={setpopoverType}
                  popoverType={popoverType}
                  popoverHeight={popoverHeight}
                  setPopoverHeight={setPopoverHeight}
                  addToCArtHandler={addToCArtHandler}
                  spec={partSpec}
                />

                <NoOtherSupplier listLength={showJobEstimator.list.length} />
              </RevxFlexLayout>
              <div
                className="comparison-drawer-popover"
                hidden={isBoolean(popoverVisible)}
                style={{ ...popoverStyle, ...containerMaxWidth }}
              ></div>
            </Form>
          </>
        )}
      </RevxSpin>
      {Boolean(showJobEstimator.list.length) && (
        <div
          className="step-actions"
          hidden={!jobEstimator || isExpert || !!jobConfigViewer}
        >
          <StepButtons />
        </div>
      )}
    </>
  );
};
